// Home.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { wp } from "../lib/wp";

const extractFirstImage = (htmlString) => {
  if (!htmlString) return null;
  const match = htmlString.match(/<img.*?src="(.*?)"/);
  return match ? match[1] : null;
};

const extractYear = (desc) => {
  if (!desc) return "";
  const match = desc.match(/\b(19|20)\d{2}\b/);
  return match ? match[0] : "";
};

// WP REST: tüm projeleri getir (gerekirse sayfalı)
async function fetchAllProjects() {
  const perPage = 100;
  let page = 1;
  let all = [];
  while (true) {
    const res = await fetch(
      wp(`/wp/v2/project?status=publish&per_page=${perPage}&page=${page}`)
    );
    if (!res.ok) break;
    const batch = await res.json();
    all = all.concat(batch);
    if (batch.length < perPage) break;
    page += 1;
  }
  return all;
}

const Home = () => {
  const [projects, setProjects] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAllProjects().then(setProjects).catch(console.error);
  }, []);

  useEffect(() => {
    if (!projects.length) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % projects.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [projects]);

  const currentProject = projects[currentIndex] || null;

  const imageUrl =
    currentProject?.project_cover_image?.url ||
    extractFirstImage(currentProject?.project_description) ||
    "https://via.placeholder.com/1200x800";
  const year = extractYear(currentProject?.project_year);
  const title = currentProject?.title?.rendered;

  const variants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  return (
    <div className="bg-[#F8F6ED] text-gray-800 min-h-screen">
      <div className="mx-auto max-w-7xl w-full px-4 py-10">
        {/* Üst büyük slider */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 1 }}
            className="flex flex-col items-center"
          >
            <div className="w-full h-[600px] overflow-hidden rounded-xl shadow-2xl border border-[#e0e0dc] relative group">
              <img
                src={imageUrl}
                alt={title}
                onClick={() => navigate(`/project/${currentProject?.slug}`)}
                className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/60 to-transparent p-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="text-white"
                >
                  <p className="text-sm text-gray-200 mb-1">{year}</p>
                  <h2 className="text-2xl font-semibold">{title}</h2>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Alt: küçük kartlar */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => {
            const img =
              project.project_cover_image?.url ||
              extractFirstImage(project.project_description) ||
              "https://via.placeholder.com/600x400";
            const y = extractYear(project.project_year);
            const t = project.title.rendered;

            return (
              <motion.div
                key={project.id}
                className="group cursor-pointer"
                onClick={() => navigate(`/project/${project.slug}`)}
                whileHover={{ y: -6, scale: 1.01 }}
                transition={{ duration: 0.25 }}
              >
                {/* Kart */}
                <div
                  className={[
                    "relative bg-white p-4 rounded-xl",
                    "border border-[#424197]",
                    "shadow-[0_10px_25px_rgba(39,40,92,0.25),0_2px_6px_rgba(39,40,92,0.15)]",
                    // 2. Değişiklik: Hover gölge rengi güncellendi. #5b5f83'ün RGB karşılığı (91,95,131)'dir.
                    "group-hover:shadow-[0_24px_60px_rgba(91,95,131,0.4),0_6px_14px_rgba(91,95,131,0.25)]",
                    "transition-all duration-300 ease-out",
                  ].join(" ")}
                >
                  {/* İç görsel kutusu */}
                  <div className="overflow-hidden rounded-md">
                    <img
                      src={img}
                      alt={t}
                      className="w-full h-56 object-cover rounded-md transition-transform duration-300 group-hover:scale-[1.02]"
                    />
                  </div>

                  <div className="mt-3 text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      {y}
                    </p>
                    <h3 className="text-sm font-semibold text-gray-800 mt-1 group-hover:text-[#1a365d] transition-colors">
                      {t}
                    </h3>
                  </div>

                  {/* zemine düşen gölge */}
                  <div className="pointer-events-none absolute inset-x-10 -bottom-3 h-6 rounded-full bg-[#27285C]/30 blur-xl opacity-50 group-hover:opacity-95 transition-opacity"></div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Home;
